/**
 * A tiny note of the student's next talk, kept on the device so the offline page
 * (service-worker.js) can still say when and where it is with no signal.
 *
 * Only the day and time, the place (or "online") and the counselor's first name.
 * Never anything about tests. Saved whenever the student's sessions load; cleared at
 * sign-out (utils/api.js clearSession removes NEXT_TALK_KEY).
 */
import { ACTIVE, endOf, startOf } from "../components/pages/studentBooking/studentBooking";

export const NEXT_TALK_KEY = "anna.nextTalk";

// A request the counselor hasn't said yes to yet isn't a planned talk
const BOOKED = ACTIVE.filter((status) => status !== "pending");
const TITLES = /^(ms|mr|mrs|miss|dr|sir|maam|ma'am|prof)\.?$/i;

export const firstName = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  const first = parts.find((part) => !TITLES.test(part));
  return first || "";
};

/** The snapshot for the soonest booked talk that hasn't ended, or null. */
export const nextTalkFrom = (appointments, now = new Date()) => {
  const next = (appointments || [])
    .filter((row) => row && BOOKED.includes(row.status) && endOf(row) > now)
    .sort((a, b) => startOf(a) - startOf(b))[0];
  if (!next) return null;
  const start = startOf(next);
  if (Number.isNaN(start.getTime())) return null;
  return {
    date: start.toISOString(),
    place: next.mode === "online" ? "online" : String(next.location || "").trim().slice(0, 80),
    counselor: firstName(next.counselor?.name).slice(0, 40),
  };
};

/** Save (or clear, when nothing is coming up) the snapshot from a list of the student's sessions. */
export const saveNextTalk = (appointments, now = new Date()) => {
  try {
    const snapshot = nextTalkFrom(appointments, now);
    if (snapshot) localStorage.setItem(NEXT_TALK_KEY, JSON.stringify(snapshot));
    else localStorage.removeItem(NEXT_TALK_KEY);
    return snapshot;
  } catch (error) {
    // Private mode or full storage: the offline page just won't show the card
    return null;
  }
};

export const clearNextTalk = () => {
  try {
    localStorage.removeItem(NEXT_TALK_KEY);
  } catch (error) {
    // nothing to clear
  }
};

export const readNextTalk = () => {
  try {
    return JSON.parse(localStorage.getItem(NEXT_TALK_KEY) || "null");
  } catch (error) {
    return null;
  }
};
